/* eslint-disable complexity */
/* eslint-disable max-lines */
import DOMPurify from 'dompurify';
import { ArrowRight, Maximize, Minimize, Minimize2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { type Chat, useChat } from '@/components/providers/ChatProvider';
import { useTheme } from '@/components/providers/ThemeProvider';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

import { AddToSlackButton } from './global/InstallSlack';
import { useFeatureFlags } from './providers/FeatureFlagsProvider';

interface Props {
  chat: Chat;
  defaultProjectId: string;
}

export const ChatContainer = ({
  chat,
  defaultProjectId,
}: Props): JSX.Element => {
  const { formatMessage } = useIntl();
  const { chatWithAnnie, prompt, setPrompt } = useChat();
  const { FeatureAskAnnie } = useFeatureFlags();

  const { resolvedTheme } = useTheme();
  const logoSrc =
    resolvedTheme === 'dark' ? '/AnnieWhite.svg' : '/AnnieBlack.svg';

  const [layout, setLayout] = useState<
    'minimized' | 'maximized' | 'fullscreen'
  >('minimized');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
    if (chat.messages.length > 0) {
      setLayout(l => (l === 'minimized' ? 'maximized' : l));
    }
  }, [chat]);

  useEffect(() => {
    if (prompt === '') {
      return;
    }

    setLayout(l => (l === 'minimized' ? 'maximized' : l));
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [prompt]);

  if (FeatureAskAnnie !== true) {
    return <></>;
  }

  return (
    <div
      className={cn(
        'fixed h-full flex flex-col items-end justify-end',
        'transition-all duration-500 ease-in-out',
        'pointer-events-none',
        layout === 'fullscreen' ? 'bottom-0 right-0' : 'bottom-4 right-4',
      )}
    >
      <Card
        className={cn(
          'pointer-events-auto',
          'shadow-lg overflow-hidden',
          'transition-all duration-500 ease-in-out',
          layout === 'minimized'
            ? 'max-h-16 max-w-16 min-w-0 min-h-0 rounded-2xl'
            : layout === 'fullscreen'
              ? 'min-h-[calc(100%-56px)] lg:min-w-[calc(100vw-12em)] min-w-100 max-h-[calc(100%-56px)] lg:max-w-[calc(100vw-12em)] max-w-100 rounded-none'
              : 'max-h-[500px] max-w-[700px] min-w-0 min-h-0 rounded-2xl',
        )}
      >
        <div
          className={cn(
            layout === 'minimized'
              ? 'h-full'
              : 'flex flex-col justify-between h-full',
          )}
        >
          <CardHeader className="p-0">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center justify-between gap-2">
                <button
                  onClick={() =>
                    setLayout(l =>
                      l === 'minimized' ? 'maximized' : 'minimized',
                    )
                  }
                  className="p-4"
                >
                  <img src={logoSrc} alt="Annie" className="w-8 h-8" />
                </button>
                {layout === 'minimized' ? (
                  <></>
                ) : (
                  <CardTitle className="text-md font-semibold flex flex-row items-center justify-between">
                    <FormattedMessage id="askAnnie.title" />
                  </CardTitle>
                )}
              </div>
              {layout === 'minimized' ? (
                <></>
              ) : (
                <div className="flex">
                  <div className="p-4 flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-lg"
                      onClick={() =>
                        setLayout(l =>
                          l === 'fullscreen' ? 'maximized' : 'fullscreen',
                        )
                      }
                    >
                      {layout === 'fullscreen' ? (
                        <Minimize className="h-6 w-6" />
                      ) : (
                        <Maximize className="h-6 w-6" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-lg"
                      onClick={() =>
                        setLayout(l =>
                          l === 'minimized' ? 'maximized' : 'minimized',
                        )
                      }
                    >
                      <Minimize2 className="h-6 w-6" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardHeader>
          <div
            className={cn(
              'transition-all duration-300 ease-in-out',
              layout === 'minimized'
                ? 'max-h-0 max-w-0'
                : 'max-h-full max-w-full',
            )}
          >
            <CardContent className="p-4 pt-0">
              <div className="flex flex-col items-center min-w-[400px]">
                {chat.messages.length > 0 ? (
                  <div
                    className={cn(
                      'flex flex-col gap-2 w-full overflow-y-auto pb-2',
                      layout === 'fullscreen'
                        ? 'max-h-[calc(100vh-220px)]'
                        : 'max-h-[320px]',
                    )}
                  >
                    {chat.messages.map(message => (
                      <div
                        key={
                          message.messageId +
                          (message.sent ? '-sent' : '-received')
                        }
                        className={`flex items-center gap-2 ${
                          message.sent ? 'justify-end' : 'justify-start'
                        }`}
                      >
                        {!message.sent && (
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="bg-muted">
                              <img
                                src={logoSrc}
                                alt="Annie"
                                className="w-4 h-4"
                              />
                            </AvatarFallback>
                          </Avatar>
                        )}
                        <div
                          className={cn(
                            'rounded-lg p-2',
                            'transition-all duration-500 ease-in-out',
                            message.sent
                              ? 'bg-blue-500 text-white'
                              : 'bg-muted',
                            layout === 'fullscreen'
                              ? 'max-w-[85%]'
                              : 'max-w-[70%]',
                          )}
                        >
                          {message.error === undefined ? (
                            <p
                              className="text-xs chat-message"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(message.content),
                              }}
                            />
                          ) : (
                            <p className="text-xs chat-message">
                              {message.error}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                    {chat.thinking && (
                      <div className={`flex items-center gap-2 justify-start`}>
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-muted">
                            <img
                              src={logoSrc}
                              alt="Annie"
                              className="w-4 h-4"
                            />
                          </AvatarFallback>
                        </Avatar>
                        <div
                          className={`rounded-lg p-2 bg-muted animate-pulse`}
                        >
                          <p className="text-xs">
                            {chat.thinking.content === undefined ? (
                              <FormattedMessage id="askAnnie.thinking" />
                            ) : (
                              formatMessage(
                                {
                                  id: 'askAnnie.thinkingWithContent',
                                },
                                { content: chat.thinking.content },
                              )
                                .split('{br}')
                                .map((line, index) => (
                                  <span key={index}>
                                    {line}
                                    {index <
                                      chat.thinking!.content!.split('{br}')
                                        .length -
                                        1 && <br />}
                                  </span>
                                ))
                            )}
                          </p>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                ) : (
                  <h3 className="text-xl pb-4">
                    <FormattedMessage id="askAnnie.welcome" />
                  </h3>
                )}
                <div className="w-full relative">
                  <div className="absolute w-full h-4 -top-4 bg-gradient-to-b from-transparent to-background" />
                  <form
                    onSubmit={e => {
                      e.preventDefault();
                      e.stopPropagation();

                      if (prompt === '') {
                        return;
                      }

                      chatWithAnnie({
                        message: prompt,
                        projectId: defaultProjectId,
                      });
                      setPrompt('');
                    }}
                    className="bg-muted rounded-lg p-2 w-full"
                  >
                    <input
                      className="bg-muted focus:outline-none w-full text-xs"
                      placeholder={formatMessage({
                        id: 'askAnnie.placeholder',
                      })}
                      value={prompt}
                      onChange={e => setPrompt(e.target.value)}
                      ref={inputRef}
                    />
                    <div className="flex items-center justify-between mt-2 flex-row-reverse">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        type="submit"
                        disabled={chat.thinking !== undefined}
                      >
                        <ArrowRight className="h-6 w-6" />
                      </Button>
                      <div className="justify-start">
                        <AddToSlackButton variant="chat" />
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </CardContent>
          </div>
        </div>
      </Card>
    </div>
  );
};
